/**
 * BrandTheme — per-tenant whitelabel contract for the SPA shell.
 *
 * Multi-tenant design:
 * - One BrandTheme per tenant Host (django-tenants Domain → Tenant). Different
 *   clients must be able to look completely different (logo, name, colors, chrome,
 *   buttons) without forking CSS or hardcoding Clinch colors in feature screens.
 * - Shell + admin UI consume CSS vars / semantic tokens only (`--clinch-*`,
 *   `--sidebar-*`, `--navbar-*`, `--btn-*`). Logo containers stay transparent so
 *   client artwork (incl. alpha) composites on the tenant sidebar.
 * - DEFAULT_CLINCH_THEME is the platform default. Platform theme UI edits the
 *   theme for the *current* Host; each tenant domain keeps its own appearance.
 * - Persist today: host-scoped localStorage. Tomorrow: GET/PUT TenantBranding by
 *   Host — same JSON shape (`appName`, `logoUrl`, `colors`, `sidebar`, `navbar`,
 *   `buttons`, `text` heading/body/muted, optional `pageBg`, `typography`).
 *   Do not invent parallel branding shapes.
 */

import { getSaasPlatformDomain, isTenantWorkspaceHost } from '@/utils/saasPlatform'

/** Legacy unscoped key (migrated into host-scoped keys on read). */
export const BRAND_THEME_STORAGE_KEY = 'clinch.brandTheme'

export const BRAND_THEME_STORAGE_KEY_PREFIX = 'clinch.brandTheme'

/** Hostname used to scope branding (matches django-tenants Domain lookup). */
export function getBrandThemeHost() {
  if (typeof window === 'undefined') return 'default'
  return String(window.location.hostname || 'default').toLowerCase()
}

/** Title-case a workspace slug for sidebar/login when no custom appName is set. */
export function formatWorkspaceSlugLabel(slug) {
  const raw = String(slug || '').trim()
  if (!raw) return ''
  return raw
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Human label from the current Host (tenant slug or domain).
 * e.g. kasi.platform.localhost → "Kasi", acme.clinchsa.co.za → "Acme".
 */
export function getTenantOrDomainBrandName(host = getBrandThemeHost()) {
  const h = String(host || '').toLowerCase()
  if (!h || h === 'default') return ''
  if (h.endsWith('.platform.localhost')) {
    return formatWorkspaceSlugLabel(h.slice(0, -'.platform.localhost'.length))
  }
  const platformDomain = getSaasPlatformDomain()
  if (h.endsWith(`.${platformDomain}`)) {
    const slug = h.slice(0, -(platformDomain.length + 1))
    if (slug && !slug.includes('.')) return formatWorkspaceSlugLabel(slug)
  }
  const first = h.split('.')[0]
  if (first && first !== 'www' && first !== 'platform') {
    return formatWorkspaceSlugLabel(first)
  }
  return h
}

/**
 * Sidebar / shell brand name: custom theme appName when set;
 * on tenant Hosts fall back to tenant/domain label instead of "Clinch".
 */
export function resolveShellBrandName(themeAppName, { host = getBrandThemeHost(), tenantHost } = {}) {
  const named = typeof themeAppName === 'string' ? themeAppName.trim() : ''
  const onTenant = tenantHost !== undefined ? Boolean(tenantHost) : isTenantWorkspaceHost(host)
  if (onTenant) {
    if (named && named.toLowerCase() !== 'clinch') return named
    return getTenantOrDomainBrandName(host) || named || 'Workspace'
  }
  return named || DEFAULT_CLINCH_THEME.appName
}


/** localStorage key for one tenant Host’s BrandTheme. */
export function brandThemeStorageKey(host = getBrandThemeHost()) {
  return `${BRAND_THEME_STORAGE_KEY_PREFIX}:${host || 'default'}`
}

export const SIDEBAR_STYLE_OPTIONS = [
  { value: 'light', label: 'Light', hint: 'Pale wash on the current page' },
  { value: 'ink', label: 'Ink', hint: 'Section labels, solid navy pill, identity at the bottom' },
  { value: 'slate', label: 'Slate', hint: 'Inset gray rail, identity docked at the bottom' },
  { value: 'outline', label: 'Outline', hint: 'Airy spacing, navy outline on the current page' },
  { value: 'navy', label: 'Navy', hint: 'Brand navy rail' },
  { value: 'graphite', label: 'Graphite', hint: 'Charcoal sections, white active pill' },
  { value: 'primary', label: 'Primary', hint: 'Brand colour rail' },
  { value: 'cards', label: 'Cards', hint: 'Collapsed rail plus a card launcher ordered by daily use' },
]

export function isSidebarStyle(value) {
  return SIDEBAR_STYLE_OPTIONS.some((opt) => opt.value === value)
}

/** Placement + chrome applied when a Style chip is picked (colours still come from the style). */
export const SIDEBAR_STYLE_RECIPES = {
  light: {
    layout: 'inline',
    radius: 'soft',
    density: 'comfortable',
    profileStyle: 'panel',
    profilePosition: 'auto',
  },
  ink: {
    layout: 'sections',
    radius: 'pill',
    density: 'comfortable',
    profileStyle: 'minimal',
    profilePosition: 'auto',
  },
  slate: {
    layout: 'dock',
    radius: 'soft',
    density: 'comfortable',
    profileStyle: 'card',
    profilePosition: 'auto',
  },
  outline: {
    layout: 'dock',
    radius: 'soft',
    density: 'comfortable',
    profileStyle: 'minimal',
    profilePosition: 'auto',
  },
  navy: {
    layout: 'inline',
    radius: 'soft',
    density: 'comfortable',
    profileStyle: 'panel',
    profilePosition: 'auto',
  },
  graphite: {
    layout: 'sections',
    radius: 'pill',
    density: 'compact',
    profileStyle: 'pill',
    profilePosition: 'auto',
  },
  primary: {
    layout: 'inline',
    radius: 'soft',
    density: 'comfortable',
    profileStyle: 'panel',
    profilePosition: 'auto',
  },
  cards: {
    layout: 'cards',
    radius: 'soft',
    density: 'comfortable',
    profileStyle: 'panel',
    profilePosition: 'navbar',
  },
}

export const NAVBAR_STYLE_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'navy', label: 'Navy' },
  { value: 'primary', label: 'Primary' },
  { value: 'custom', label: 'Custom' },
]

export const SIDEBAR_RADIUS_OPTIONS = [
  { value: 'soft', label: 'Soft' },
  { value: 'pill', label: 'Pill' },
  { value: 'square', label: 'Square' },
]

export const SIDEBAR_DENSITY_OPTIONS = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
]

export const SIDEBAR_LAYOUT_OPTIONS = [
  { value: 'inline', label: 'Inline', hint: 'Icon beside label' },
  { value: 'compact', label: 'Slim', hint: 'Narrower rail, tighter type' },
  { value: 'stack', label: 'Icon top', hint: 'Label under each icon' },
  { value: 'sections', label: 'Sections', hint: 'Uppercase group titles, items listed under them' },
  { value: 'dock', label: 'Dock', hint: 'Identity sits at the bottom of the rail' },
  { value: 'cards', label: 'Cards', hint: 'Collapsed rail; nav items become a card launcher ordered by daily use' },
]

export function isSidebarLayout(value) {
  return SIDEBAR_LAYOUT_OPTIONS.some((opt) => opt.value === value)
}

/** True when the left rail is replaced by the Apps card launcher. */
export function isCardsNavLayout(layout) {
  return layout === 'cards'
}

/** User identity block in the sidebar (and Icon-top navbar chip). */
export const SIDEBAR_PROFILE_STYLE_OPTIONS = [
  { value: 'panel', label: 'Panel' },
  { value: 'pill', label: 'Pill' },
  { value: 'card', label: 'Card' },
  { value: 'minimal', label: 'Minimal' },
]

/** Team Inbox (/manage/inbox) layout. */
export const INBOX_LAYOUT_OPTIONS = [
  { value: 'classic', label: 'Classic' },
  { value: 'modern', label: 'Redesign' },
]

/** Team Inbox colour theme (Redesign layout). `default` follows the workspace brand colours. */
export const INBOX_THEME_OPTIONS = [
  { value: 'default', label: 'Default', dark: false },
  { value: 'whatsapp', label: 'WhatsApp', dark: false },
  { value: 'dark', label: 'Dark', dark: true },
  { value: 'midnight', label: 'Midnight', dark: true },
]

/** Where the signed-in profile chip is shown. */
export const SIDEBAR_PROFILE_POSITION_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'sidebar', label: 'Top' },
  { value: 'footer', label: 'Bottom' },
  { value: 'navbar', label: 'Navbar' },
]

/**
 * Resolve whether the profile chip belongs in the sidebar and/or navbar.
 * Auto: top of sidebar for Inline/Slim, bottom for Sections/Dock, navbar for Icon top / Cards.
 */
export function resolveProfilePlacement(sidebar) {
  const layout = sidebar?.layout || 'inline'
  const position = sidebar?.profilePosition || 'auto'
  if (position === 'navbar') {
    return { showInSidebar: false, showInNavbar: true, slot: 'top' }
  }
  if (position === 'footer') {
    return { showInSidebar: true, showInNavbar: false, slot: 'footer' }
  }
  if (position === 'sidebar') {
    return { showInSidebar: true, showInNavbar: false, slot: 'top' }
  }
  if (layout === 'stack' || layout === 'cards') {
    return { showInSidebar: false, showInNavbar: true, slot: 'top' }
  }
  if (layout === 'sections' || layout === 'dock') {
    return { showInSidebar: true, showInNavbar: false, slot: 'footer' }
  }
  return { showInSidebar: true, showInNavbar: false, slot: 'top' }
}

/** CSS width for each sidebar layout (expanded, non-collapsed). */
export function sidebarWidthForLayout(layout) {
  if (layout === 'stack') return '120px'
  if (layout === 'compact') return '168px'
  return '248px'
}

export const DEFAULT_CLINCH_THEME = Object.freeze({
  appName: 'Clinch',
  logoUrl: '/assets/images/brand-refresh/clinch-logo-refresh.png',
  colors: Object.freeze({
    primary: '#33B5FF',
    primaryDark: '#0081C9',
    navy: '#001F3F',
    accent: '#00E676',
    blue: '#0066CC',
    blueDark: '#003366',
  }),
  sidebar: Object.freeze({
    style: 'light',
    radius: 'soft',
    density: 'comfortable',
    layout: 'inline',
    profileStyle: 'panel',
    profilePosition: 'auto',
  }),
  inbox: Object.freeze({
    layout: 'classic',
    theme: 'default',
  }),
  navbar: Object.freeze({
    style: 'light',
    background: '#FFFFFF',
    backgroundEnd: '#F1F5F9',
    text: '#001F3F',
  }),
  buttons: Object.freeze({
    primary: '#33B5FF',
    primaryEnd: '#0081C9',
    accent: '#0081C9',
    accentEnd: '#001F3F',
    success: '#00C853',
    successEnd: '#009624',
    danger: '#EF4444',
    dangerEnd: '#B91C1C',
    warning: '#F59E0B',
    warningEnd: '#D97706',
    secondary: '#E2E8F0',
    secondaryText: '#001F3F',
    ghost: '#FFFFFF',
    ghostText: '#001F3F',
    ghostBorder: '#D0D7E2',
    navIcon: '#FFFFFF',
    navIconText: '#001F3F',
    navIconBorder: '#D0D7E2',
    navIconHover: '#E6F6FF',
    navIconHoverText: '#33B5FF',
    navIconBadge: '#FF6B35',
    navIconBadgeEnd: '#E55A2B',
  }),
  text: Object.freeze({
    heading: '#001F3F',
    body: '#000000',
    muted: '#64748B',
  }),
})

/** Page copy tokens on Appearance → Text (not button / navbar / status colours). */
export const TEXT_COLOR_FIELDS = [
  { key: 'heading', label: 'Headings' },
  { key: 'body', label: 'Body' },
  { key: 'muted', label: 'Muted / secondary' },
]

export const BUTTON_COLOR_FIELDS = [
  { key: 'primary', label: 'Primary' },
  { key: 'primaryEnd', label: 'Primary end' },
  { key: 'accent', label: 'Accent' },
  { key: 'accentEnd', label: 'Accent end' },
  { key: 'success', label: 'Success' },
  { key: 'successEnd', label: 'Success end' },
  { key: 'danger', label: 'Danger' },
  { key: 'dangerEnd', label: 'Danger end' },
  { key: 'warning', label: 'Warning' },
  { key: 'warningEnd', label: 'Warning end' },
  { key: 'secondary', label: 'Secondary bg' },
  { key: 'secondaryText', label: 'Secondary text' },
  { key: 'ghost', label: 'Ghost bg' },
  { key: 'ghostText', label: 'Ghost text' },
  { key: 'ghostBorder', label: 'Ghost border' },
  { key: 'navIcon', label: 'Nav icon bg' },
  { key: 'navIconText', label: 'Nav icon text' },
  { key: 'navIconBorder', label: 'Nav icon border' },
  { key: 'navIconHover', label: 'Nav icon hover bg' },
  { key: 'navIconHoverText', label: 'Nav icon hover text' },
  { key: 'navIconBadge', label: 'Nav icon badge' },
  { key: 'navIconBadgeEnd', label: 'Nav icon badge end' },
]

/** CSS custom properties controlled by BrandTheme colors */
const COLOR_VAR_MAP = {
  primary: '--clinch-blue-primary',
  primaryDark: '--clinch-blue-refresh-dark',
  navy: '--clinch-navy',
  accent: '--clinch-green-primary',
  blue: '--clinch-blue',
  blueDark: '--clinch-blue-dark',
}

const RADIUS_MAP = {
  soft: '0.5rem',
  pill: '999px',
  square: '4px',
}

const DENSITY_PAD = {
  comfortable: '0.45rem 0.75rem',
  compact: '0.32rem 0.6rem',
}

const NAVBAR_CSS_PROPS = [
  '--navbar-bg-start',
  '--navbar-bg-mid',
  '--navbar-bg-end',
  '--navbar-fg',
  '--navbar-fg-muted',
  '--navbar-border',
  '--navbar-active-bg',
  '--navbar-active-fg',
  '--navbar-hover-bg',
]

const SIDEBAR_CSS_PROPS = [
  '--sidebar-bg-start',
  '--sidebar-bg-mid',
  '--sidebar-bg-end',
  '--sidebar-fg',
  '--sidebar-fg-muted',
  '--sidebar-border',
  '--sidebar-active-bg',
  '--sidebar-active-fg',
  '--sidebar-active-border',
  '--sidebar-active-shadow',
  '--sidebar-indicator-opacity',
  '--sidebar-hover-bg',
  '--sidebar-item-radius',
  '--sidebar-item-padding',
  '--sidebar-width',
]

function normalizeHex(value, fallback) {
  if (typeof value !== 'string') return fallback
  let hex = value.trim()
  if (!hex) return fallback
  if (hex[0] !== '#') hex = `#${hex}`
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return fallback
  return hex.toUpperCase()
}

export function hexToRgbChannels(hex) {
  const h = normalizeHex(hex, '#000000').slice(1)
  return `${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}`
}

/** Shift hex toward white (positive) or black (negative). amount roughly -1…1. */
export function shadeHex(hex, amount, fallback = '#000000') {
  const h = normalizeHex(hex, fallback).slice(1)
  const clamp = (n) => Math.min(255, Math.max(0, n))
  const shift = (ch) =>
    clamp(Math.round(parseInt(ch, 16) + amount * 255))
      .toString(16)
      .padStart(2, '0')
  return `#${shift(h.slice(0, 2))}${shift(h.slice(2, 4))}${shift(h.slice(4, 6))}`.toUpperCase()
}

/** Readable ink (navy or white) for a background hex. */
export function contrastInkHex(bgHex) {
  const h = normalizeHex(bgHex, '#000000').slice(1)
  const channel = (i) => parseInt(h.slice(i, i + 2), 16) / 255
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  const L = 0.2126 * lin(channel(0)) + 0.7152 * lin(channel(2)) + 0.0722 * lin(channel(4))
  return L > 0.55 ? '#001F3F' : '#FFFFFF'
}

function relativeLuminance(hex) {
  const h = normalizeHex(hex, '#000000').slice(1)
  const lin = (i) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4)
}

/** WCAG contrast ratio between two hex colors (1 = identical, 21 = black on white). */
export function contrastRatio(aHex, bHex) {
  const a = relativeLuminance(aHex)
  const b = relativeLuminance(bHex)
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

/** Navy or white — whichever reads better on the background. */
export function readableInkHex(bgHex) {
  return contrastRatio('#001F3F', bgHex) >= contrastRatio('#FFFFFF', bgHex) ? '#001F3F' : '#FFFFFF'
}

/** Button text tokens and the background they sit on. */
export const BUTTON_TEXT_PAIRS = [
  { text: 'secondaryText', bg: 'secondary', label: 'Secondary' },
  { text: 'ghostText', bg: 'ghost', label: 'Ghost' },
  { text: 'navIconText', bg: 'navIcon', label: 'Nav icon' },
]

/** Below this ratio button text is treated as unreadable and auto-corrected. */
export const MIN_BUTTON_TEXT_CONTRAST = 3

export function findLowContrastButtonText(buttons = {}) {
  return BUTTON_TEXT_PAIRS.filter(({ text, bg }) => {
    if (!buttons[text] || !buttons[bg]) return false
    return contrastRatio(buttons[text], buttons[bg]) < MIN_BUTTON_TEXT_CONTRAST
  }).map((pair) => ({
    ...pair,
    ratio: contrastRatio(buttons[pair.text], buttons[pair.bg]),
    suggested: readableInkHex(buttons[pair.bg]),
  }))
}

export function ensureReadableButtonText(buttons = {}) {
  const out = { ...buttons }
  findLowContrastButtonText(buttons).forEach(({ text, suggested }) => {
    out[text] = suggested
  })
  return out
}

/**
 * Paint every button token from one brand color.
 * Fills use the color; *End darkens for gradients; *Text uses contrast ink; *Border darkens.
 */
export function applyColorToAllButtonTokens(hex) {
  const fill = normalizeHex(hex, DEFAULT_CLINCH_THEME.buttons.primary)
  const end = shadeHex(fill, -0.18, fill)
  const ink = readableInkHex(fill)
  const border = shadeHex(fill, -0.25, fill)
  const hoverBg = shadeHex(fill, 0.42, fill)
  const out = {}
  Object.keys(DEFAULT_CLINCH_THEME.buttons).forEach((key) => {
    if (key === 'navIconHoverText') {
      out[key] = fill
    } else if (key === 'navIconHover') {
      out[key] = hoverBg
    } else if (/Text$/i.test(key)) {
      out[key] = ink
    } else if (/Border$/i.test(key)) {
      out[key] = border
    } else if (/End$/i.test(key)) {
      out[key] = end
    } else {
      out[key] = fill
    }
  })
  return out
}

function surfaceFromStyle(style, colors) {
  const { primary, primaryDark, navy, blue } = colors
  const active = blue || primary
  const navyRgb = hexToRgbChannels(navy)
  const primaryRgb = hexToRgbChannels(primary)
  const activeRgb = hexToRgbChannels(active)
  if (style === 'navy') {
    return {
      bgStart: navy,
      bgMid: navy,
      bgEnd: '#00152B',
      fg: '#FFFFFF',
      fgMuted: 'rgba(255, 255, 255, 0.72)',
      border: 'rgba(255, 255, 255, 0.12)',
      activeBg: `rgba(${activeRgb}, 0.22)`,
      activeFg: active,
      hoverBg: 'rgba(255, 255, 255, 0.08)',
      activeBorder: 'transparent',
      activeShadow: 'none',
      indicatorOpacity: '1',
    }
  }
  if (style === 'primary') {
    return {
      bgStart: primary,
      bgMid: primaryDark,
      bgEnd: navy,
      fg: '#FFFFFF',
      fgMuted: 'rgba(255, 255, 255, 0.78)',
      border: 'rgba(255, 255, 255, 0.16)',
      activeBg: 'rgba(255, 255, 255, 0.22)',
      activeFg: '#FFFFFF',
      hoverBg: 'rgba(255, 255, 255, 0.12)',
      activeBorder: 'transparent',
      activeShadow: '0 4px 14px rgba(0, 0, 0, 0.12)',
      indicatorOpacity: '1',
    }
  }
  if (style === 'ink') {
    return {
      bgStart: '#FFFFFF',
      bgMid: '#FFFFFF',
      bgEnd: '#F8FAFC',
      fg: navy,
      fgMuted: `rgba(${navyRgb}, 0.62)`,
      border: `rgba(${navyRgb}, 0.10)`,
      activeBg: navy,
      activeFg: '#FFFFFF',
      hoverBg: `rgba(${navyRgb}, 0.06)`,
      activeBorder: 'transparent',
      activeShadow: `0 8px 18px rgba(${navyRgb}, 0.22)`,
      indicatorOpacity: '0',
    }
  }
  if (style === 'slate') {
    return {
      bgStart: '#F8FAFC',
      bgMid: '#F1F5F9',
      bgEnd: '#E8EEF4',
      fg: navy,
      fgMuted: `rgba(${navyRgb}, 0.58)`,
      border: `rgba(${navyRgb}, 0.10)`,
      activeBg: '#E2E8F0',
      activeFg: navy,
      hoverBg: '#E2E8F0',
      activeBorder: 'transparent',
      activeShadow: `0 1px 2px rgba(${navyRgb}, 0.08)`,
      indicatorOpacity: '0',
    }
  }
  if (style === 'outline') {
    return {
      bgStart: '#FFFFFF',
      bgMid: '#FFFFFF',
      bgEnd: '#F8FAFC',
      fg: navy,
      fgMuted: `rgba(${navyRgb}, 0.62)`,
      border: `rgba(${navyRgb}, 0.10)`,
      activeBg: `rgba(${navyRgb}, 0.04)`,
      activeFg: navy,
      hoverBg: `rgba(${navyRgb}, 0.05)`,
      activeBorder: navy,
      activeShadow: 'none',
      indicatorOpacity: '0',
    }
  }
  if (style === 'graphite') {
    return {
      bgStart: '#1E293B',
      bgMid: '#1E293B',
      bgEnd: '#0F172A',
      fg: '#F1F5F9',
      fgMuted: 'rgba(241, 245, 249, 0.68)',
      border: 'rgba(255, 255, 255, 0.10)',
      activeBg: '#FFFFFF',
      activeFg: '#0F172A',
      hoverBg: 'rgba(255, 255, 255, 0.08)',
      activeBorder: 'transparent',
      activeShadow: '0 8px 20px rgba(0, 0, 0, 0.28)',
      indicatorOpacity: '0',
    }
  }
  return {
    bgStart: '#FFFFFF',
    bgMid: '#F8FAFC',
    bgEnd: '#F1F5F9',
    fg: navy,
    fgMuted: `rgba(${navyRgb}, 0.65)`,
    border: `rgba(${navyRgb}, 0.08)`,
    activeBg: `rgba(${primaryRgb}, 0.14)`,
    activeFg: primary,
    hoverBg: `rgba(${primaryRgb}, 0.06)`,
    activeBorder: 'transparent',
    activeShadow: `0 4px 14px rgba(${primaryRgb}, 0.12)`,
    indicatorOpacity: '1',
  }
}

function normalizeSidebar(partial) {
  const base = DEFAULT_CLINCH_THEME.sidebar
  const style = isSidebarStyle(partial?.style) ? partial.style : base.style
  const radius = ['soft', 'pill', 'square'].includes(partial?.radius)
    ? partial.radius
    : base.radius
  const density = ['comfortable', 'compact'].includes(partial?.density)
    ? partial.density
    : base.density
  const layout = isSidebarLayout(partial?.layout) ? partial.layout : base.layout
  const profileStyle = ['panel', 'pill', 'card', 'minimal'].includes(partial?.profileStyle)
    ? partial.profileStyle
    : base.profileStyle
  const profilePosition = ['auto', 'sidebar', 'footer', 'navbar'].includes(partial?.profilePosition)
    ? partial.profilePosition
    : base.profilePosition
  const backgroundEnd = normalizeHex(partial?.backgroundEnd, '')
  return backgroundEnd
    ? { style, radius, density, layout, profileStyle, profilePosition, backgroundEnd }
    : { style, radius, density, layout, profileStyle, profilePosition }
}

function normalizeInbox(partial) {
  const base = DEFAULT_CLINCH_THEME.inbox
  const layout = INBOX_LAYOUT_OPTIONS.some((o) => o.value === partial?.layout)
    ? partial.layout
    : base.layout
  const theme = INBOX_THEME_OPTIONS.some((o) => o.value === partial?.theme)
    ? partial.theme
    : base.theme
  return { layout, theme }
}

function normalizeNavbar(partial, colors) {
  const base = DEFAULT_CLINCH_THEME.navbar
  const style = ['light', 'navy', 'primary', 'custom'].includes(partial?.style)
    ? partial.style
    : base.style

  if (style === 'custom') {
    return {
      style,
      background: normalizeHex(partial?.background, base.background),
      backgroundEnd: normalizeHex(partial?.backgroundEnd, base.backgroundEnd),
      text: normalizeHex(partial?.text, base.text),
    }
  }

  const surface = surfaceFromStyle(style, colors)
  return {
    style,
    background: surface.bgStart,
    backgroundEnd: surface.bgEnd,
    text: surface.fg,
  }
}

function normalizeButtons(partial) {
  const base = DEFAULT_CLINCH_THEME.buttons
  const src = partial || {}
  const out = {}
  Object.keys(base).forEach((key) => {
    out[key] = normalizeHex(src[key], base[key])
  })
  return out
}

function normalizeText(partial, colors) {
  const base = DEFAULT_CLINCH_THEME.text
  const src = partial || {}
  return {
    heading: normalizeHex(src.heading, colors?.navy || base.heading),
    body: normalizeHex(src.body, base.body),
    muted: normalizeHex(src.muted, base.muted),
  }
}

export function normalizeBrandTheme(partial) {
  const base = DEFAULT_CLINCH_THEME
  const colorsIn = (partial && partial.colors) || {}
  const colors = {
    primary: normalizeHex(colorsIn.primary, base.colors.primary),
    primaryDark: normalizeHex(colorsIn.primaryDark, base.colors.primaryDark),
    navy: normalizeHex(colorsIn.navy, base.colors.navy),
    accent: normalizeHex(colorsIn.accent, base.colors.accent),
    blue: normalizeHex(colorsIn.blue, base.colors.blue),
    blueDark: normalizeHex(colorsIn.blueDark, base.colors.blueDark),
  }
  const pageBgRaw = typeof partial?.pageBg === 'string' ? partial.pageBg.trim() : ''
  const suggestionRaw = partial?.suggestionId || partial?.suggestion_id || ''
  const suggestionId =
    typeof suggestionRaw === 'string' && suggestionRaw.trim() ? suggestionRaw.trim() : ''
  return {
    suggestionId,
    appName:
      typeof partial?.appName === 'string' && partial.appName.trim()
        ? partial.appName.trim()
        : base.appName,
    logoUrl:
      partial && Object.prototype.hasOwnProperty.call(partial, 'logoUrl') && !(partial.logoUrl || '').trim()
        ? ''
        : typeof partial?.logoUrl === 'string' && partial.logoUrl.trim()
          ? partial.logoUrl.trim()
          : base.logoUrl,
    colors,
    sidebar: normalizeSidebar(partial?.sidebar),
    inbox: normalizeInbox(partial?.inbox),
    navbar: normalizeNavbar(partial?.navbar, colors),
    buttons: normalizeButtons(partial?.buttons),
    text: normalizeText(partial?.text, colors),
    pageBg: pageBgRaw ? normalizeHex(pageBgRaw, '') : '',
    typography: partial?.typography === 'corporate' ? 'corporate' : '',
  }
}

function applySurfaceVars(root, prefix, surface) {
  root.style.setProperty(`--${prefix}-bg-start`, surface.bgStart)
  root.style.setProperty(`--${prefix}-bg-mid`, surface.bgMid)
  root.style.setProperty(`--${prefix}-bg-end`, surface.bgEnd)
  root.style.setProperty(`--${prefix}-fg`, surface.fg)
  root.style.setProperty(`--${prefix}-fg-muted`, surface.fgMuted)
  root.style.setProperty(`--${prefix}-border`, surface.border)
  root.style.setProperty(`--${prefix}-active-bg`, surface.activeBg)
  root.style.setProperty(`--${prefix}-active-fg`, surface.activeFg)
  root.style.setProperty(`--${prefix}-hover-bg`, surface.hoverBg)
}

function isHarborSkyTheme(theme) {
  return theme.colors.navy === '#0F172A' && theme.colors.primary === '#0EA5E9'
}

function applySidebarSurface(root, theme) {
  const { style } = theme.sidebar

  root.dataset.sidebarStyle = style
  root.dataset.sidebarRadius = theme.sidebar.radius
  root.dataset.sidebarDensity = theme.sidebar.density
  root.dataset.sidebarLayout = theme.sidebar.layout
  root.dataset.sidebarProfileStyle = theme.sidebar.profileStyle || 'panel'
  root.dataset.sidebarProfilePosition = theme.sidebar.profilePosition || 'auto'

  root.style.setProperty('--sidebar-item-radius', RADIUS_MAP[theme.sidebar.radius])
  root.style.setProperty('--sidebar-item-padding', DENSITY_PAD[theme.sidebar.density])
  root.style.setProperty('--sidebar-width', sidebarWidthForLayout(theme.sidebar.layout))

  const surface = surfaceFromStyle(style, theme.colors)
  if (theme.sidebar.backgroundEnd && style === 'navy') {
    surface.bgStart = theme.colors.navy
    surface.bgMid = theme.colors.navy
    surface.bgEnd = theme.sidebar.backgroundEnd
  } else if (theme.typography === 'corporate' && style === 'navy') {
    surface.bgMid = theme.colors.navy
    surface.bgEnd = theme.colors.navy
  }
  if (isHarborSkyTheme(theme) && style === 'navy') {
    surface.fgMuted = '#A9B4C9'
    surface.activeBg = 'linear-gradient(90deg, rgba(14, 165, 233, 0.18), rgba(34, 197, 94, 0.12))'
    surface.activeFg = '#5EEAD4'
    root.style.setProperty('--sidebar-logout', '#F87171')
  } else {
    root.style.removeProperty('--sidebar-logout')
  }
  applySurfaceVars(root, 'sidebar', surface)
  root.style.setProperty('--sidebar-active-border', surface.activeBorder || 'transparent')
  root.style.setProperty('--sidebar-active-shadow', surface.activeShadow || 'none')
  root.style.setProperty(
    '--sidebar-indicator-opacity',
    surface.indicatorOpacity == null ? '1' : String(surface.indicatorOpacity)
  )
}

function applyNavbarSurface(root, theme) {
  const { style, background, backgroundEnd, text } = theme.navbar
  root.dataset.navbarStyle = style

  let surface
  if (style === 'custom') {
    const fgRgb = hexToRgbChannels(text)
    const isLightText =
      parseInt(text.slice(1, 3), 16) * 0.299 +
        parseInt(text.slice(3, 5), 16) * 0.587 +
        parseInt(text.slice(5, 7), 16) * 0.114 >
      160
    const primaryUpper = String(theme.colors.primary || '').toUpperCase()
    const primaryIsChrome =
      isLightText &&
      (primaryUpper === String(background || '').toUpperCase() ||
        primaryUpper === String(backgroundEnd || '').toUpperCase())
    surface = {
      bgStart: background,
      bgMid: background,
      bgEnd: backgroundEnd,
      fg: text,
      fgMuted: isLightText
        ? 'rgba(255, 255, 255, 0.72)'
        : `rgba(${fgRgb}, 0.65)`,
      border: isLightText
        ? 'rgba(255, 255, 255, 0.14)'
        : `rgba(${fgRgb}, 0.12)`,
      activeBg: primaryIsChrome
        ? 'rgba(255, 255, 255, 0.22)'
        : `rgba(${hexToRgbChannels(theme.colors.primary)}, 0.16)`,
      activeFg: primaryIsChrome ? '#FFFFFF' : theme.colors.primary,
      hoverBg: primaryIsChrome
        ? 'rgba(255, 255, 255, 0.12)'
        : `rgba(${hexToRgbChannels(theme.colors.primary)}, 0.08)`,
    }
  } else {
    surface = surfaceFromStyle(style, theme.colors)
  }

  applySurfaceVars(root, 'navbar', surface)
}

function applyButtonTokens(root, theme) {
  const b = ensureReadableButtonText(theme.buttons)
  root.style.setProperty('--btn-primary', b.primary)
  root.style.setProperty('--btn-primary-end', b.primaryEnd)
  root.style.setProperty('--btn-accent', b.accent)
  root.style.setProperty('--btn-accent-end', b.accentEnd)
  root.style.setProperty('--btn-success', b.success)
  root.style.setProperty('--btn-success-end', b.successEnd)
  root.style.setProperty('--btn-danger', b.danger)
  root.style.setProperty('--btn-danger-end', b.dangerEnd)
  root.style.setProperty('--btn-warning', b.warning)
  root.style.setProperty('--btn-warning-end', b.warningEnd)
  root.style.setProperty('--btn-secondary', b.secondary)
  root.style.setProperty('--btn-secondary-text', b.secondaryText)
  root.style.setProperty('--btn-ghost', b.ghost)
  root.style.setProperty('--btn-ghost-text', b.ghostText)
  root.style.setProperty('--btn-ghost-border', b.ghostBorder)
  root.style.setProperty('--btn-nav-icon', b.navIcon)
  root.style.setProperty('--btn-nav-icon-text', b.navIconText)
  root.style.setProperty('--btn-nav-icon-border', b.navIconBorder)
  root.style.setProperty('--btn-nav-icon-hover', b.navIconHover)
  root.style.setProperty('--btn-nav-icon-hover-text', b.navIconHoverText)
  root.style.setProperty('--btn-nav-icon-badge', b.navIconBadge)
  root.style.setProperty('--btn-nav-icon-badge-end', b.navIconBadgeEnd)
  root.style.setProperty('--btn-primary-rgb', hexToRgbChannels(b.primary))
  root.style.setProperty('--btn-accent-rgb', hexToRgbChannels(b.accentEnd))
  root.style.setProperty('--btn-success-rgb', hexToRgbChannels(b.success))
  root.style.setProperty('--btn-danger-rgb', hexToRgbChannels(b.danger))
  root.style.setProperty('--btn-warning-rgb', hexToRgbChannels(b.warning))
  root.style.setProperty('--btn-secondary-rgb', hexToRgbChannels(b.secondaryText))
  root.style.setProperty('--btn-ghost-rgb', hexToRgbChannels(b.ghostText))
  root.style.setProperty('--btn-ghost-border-rgb', hexToRgbChannels(b.ghostBorder))
  root.style.setProperty('--btn-nav-icon-rgb', hexToRgbChannels(b.navIconText))
  root.style.setProperty('--btn-nav-icon-hover-rgb', hexToRgbChannels(b.navIconHoverText))
  root.style.setProperty(
    '--btn-primary-gradient',
    `linear-gradient(135deg, ${b.primary} 0%, ${b.primaryEnd} 100%)`
  )
  root.style.setProperty(
    '--btn-accent-gradient',
    `linear-gradient(135deg, ${b.accent} 0%, ${b.accentEnd} 100%)`
  )
  root.style.setProperty(
    '--btn-success-gradient',
    `linear-gradient(135deg, ${b.success} 0%, ${b.successEnd} 100%)`
  )
  root.style.setProperty(
    '--btn-danger-gradient',
    `linear-gradient(135deg, ${b.danger} 0%, ${b.dangerEnd} 100%)`
  )
  root.style.setProperty(
    '--btn-warning-gradient',
    `linear-gradient(135deg, ${b.warning} 0%, ${b.warningEnd} 100%)`
  )
  root.style.setProperty(
    '--btn-nav-icon-badge-gradient',
    `linear-gradient(135deg, ${b.navIconBadge} 0%, ${b.navIconBadgeEnd} 100%)`
  )
}

export function loadBrandThemeFromStorage(host = getBrandThemeHost()) {
  const key = brandThemeStorageKey(host)
  try {
    let raw = localStorage.getItem(key)
    // One-time migrate from legacy unscoped key for this origin/host.
    if (!raw) {
      const legacy = localStorage.getItem(BRAND_THEME_STORAGE_KEY)
      if (legacy) {
        raw = legacy
        localStorage.setItem(key, legacy)
      }
    }
    if (!raw) return normalizeBrandTheme(DEFAULT_CLINCH_THEME)
    return normalizeBrandTheme(JSON.parse(raw))
  } catch {
    return normalizeBrandTheme(DEFAULT_CLINCH_THEME)
  }
}

export function saveBrandThemeToStorage(theme, host = getBrandThemeHost()) {
  const normalized = normalizeBrandTheme(theme)
  const key = brandThemeStorageKey(host)
  localStorage.setItem(key, JSON.stringify(normalized))
  // Keep legacy key in sync on this origin so older readers still see a theme.
  localStorage.setItem(BRAND_THEME_STORAGE_KEY, JSON.stringify(normalized))
  return normalized
}

export function clearBrandThemeStorage(host = getBrandThemeHost()) {
  localStorage.removeItem(brandThemeStorageKey(host))
  localStorage.removeItem(BRAND_THEME_STORAGE_KEY)
}

/**
 * Apply theme to :root. Sets color vars + sidebar/navbar surface vars + rgb channels.
 */
export function applyBrandTheme(theme) {
  const t = normalizeBrandTheme(theme)
  const root = document.documentElement

  Object.entries(COLOR_VAR_MAP).forEach(([key, cssVar]) => {
    root.style.setProperty(cssVar, t.colors[key])
  })

  root.style.setProperty('--clinch-primary-rgb', hexToRgbChannels(t.colors.primary))
  root.style.setProperty('--clinch-navy-rgb', hexToRgbChannels(t.colors.navy))
  root.style.setProperty(
    '--gradient-refresh-blue',
    `linear-gradient(135deg, ${t.colors.primary} 0%, ${t.colors.primaryDark} 100%)`
  )
  root.style.setProperty(
    '--gradient-primary',
    `linear-gradient(135deg, ${t.colors.blue} 0%, ${t.colors.blueDark} 100%)`
  )
  root.style.setProperty(
    '--gradient-refresh-green',
    `linear-gradient(135deg, ${t.colors.accent} 0%, ${shadeHex(t.colors.accent, -0.12, t.colors.accent)} 100%)`
  )
  root.style.setProperty('--clinch-green-dark', shadeHex(t.colors.accent, -0.12, '#00C853'))

  applySidebarSurface(root, t)
  applyNavbarSurface(root, t)
  applyButtonTokens(root, t)
  applyPageAndTypography(root, t)

  root.dataset.brandAppName = t.appName
  root.dataset.brandLogoUrl = t.logoUrl

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('brand-theme-changed', { detail: { theme: t } })
    )
  }

  return t
}

const PAGE_TYPOGRAPHY_CSS_PROPS = [
  '--clinch-page-bg',
  '--status-approved',
  '--status-pending',
  '--status-rejected',
  '--muted-foreground',
  '--ui-border',
  '--clinch-blue-refresh-light',
  '--clinch-green-light',
  '--brand-logo-avatar-bg',
  '--sidebar-logout',
]

const TEXT_CSS_PROPS = [
  '--clinch-text-heading',
  '--text-primary',
  '--text-secondary',
  '--text-light',
  '--ui-text-primary',
  '--ui-text-secondary',
  '--ui-text-tertiary',
  '--foreground',
  '--card-foreground',
  '--muted-foreground',
  '--bs-body-color',
  '--bs-secondary-color',
]

function applyTextTokens(root, theme) {
  const heading = theme.text?.heading || DEFAULT_CLINCH_THEME.text.heading
  const body = theme.text?.body || DEFAULT_CLINCH_THEME.text.body
  const muted = theme.text?.muted || DEFAULT_CLINCH_THEME.text.muted
  const mutedLight = shadeHex(muted, 0.22, muted)
  root.style.setProperty('--clinch-text-heading', heading)
  root.style.setProperty('--text-primary', body)
  root.style.setProperty('--text-secondary', muted)
  root.style.setProperty('--text-light', mutedLight)
  root.style.setProperty('--ui-text-primary', body)
  root.style.setProperty('--ui-text-secondary', muted)
  root.style.setProperty('--ui-text-tertiary', mutedLight)
  root.style.setProperty('--foreground', body)
  root.style.setProperty('--card-foreground', body)
  root.style.setProperty('--muted-foreground', muted)
  root.style.setProperty('--bs-body-color', body)
  root.style.setProperty('--bs-secondary-color', muted)
}

function applyPageAndTypography(root, theme) {
  root.style.setProperty('--status-approved', theme.buttons.success)
  root.style.setProperty('--status-pending', theme.buttons.warning)
  root.style.setProperty('--status-rejected', theme.buttons.danger)

  if (theme.pageBg) {
    root.style.setProperty('--clinch-page-bg', theme.pageBg)
    root.style.setProperty('--background', theme.pageBg)
    root.style.setProperty('--ui-bg-secondary', theme.pageBg)
  } else {
    root.style.removeProperty('--clinch-page-bg')
    root.style.removeProperty('--background')
    root.style.removeProperty('--ui-bg-secondary')
  }

  if (isHarborSkyTheme(theme)) {
    root.style.setProperty('--muted-foreground', '#64748B')
    root.style.setProperty('--ui-border', '#E5EEF5')
    root.style.setProperty('--clinch-blue-refresh-light', '#E0F2FE')
    root.style.setProperty('--clinch-green-light', '#DCFCE7')
    root.style.setProperty('--clinch-green-dark', '#16A34A')
    root.style.setProperty(
      '--brand-logo-avatar-bg',
      'linear-gradient(135deg, #0EA5E9, #22C55E)'
    )
  } else {
    root.style.removeProperty('--muted-foreground')
    root.style.removeProperty('--ui-border')
    root.style.removeProperty('--clinch-blue-refresh-light')
    root.style.removeProperty('--clinch-green-light')
    root.style.removeProperty('--brand-logo-avatar-bg')
  }

  if (theme.typography) {
    root.dataset.brandTypography = theme.typography
  } else {
    delete root.dataset.brandTypography
  }

  applyTextTokens(root, theme)
}

const BUTTON_CSS_PROPS = [
  '--btn-primary',
  '--btn-primary-end',
  '--btn-accent',
  '--btn-accent-end',
  '--btn-success',
  '--btn-success-end',
  '--btn-danger',
  '--btn-danger-end',
  '--btn-warning',
  '--btn-warning-end',
  '--btn-secondary',
  '--btn-secondary-text',
  '--btn-ghost',
  '--btn-ghost-text',
  '--btn-ghost-border',
  '--btn-nav-icon',
  '--btn-nav-icon-text',
  '--btn-nav-icon-border',
  '--btn-nav-icon-hover',
  '--btn-nav-icon-hover-text',
  '--btn-nav-icon-badge',
  '--btn-nav-icon-badge-end',
  '--btn-primary-rgb',
  '--btn-accent-rgb',
  '--btn-success-rgb',
  '--btn-danger-rgb',
  '--btn-warning-rgb',
  '--btn-secondary-rgb',
  '--btn-ghost-rgb',
  '--btn-ghost-border-rgb',
  '--btn-nav-icon-rgb',
  '--btn-nav-icon-hover-rgb',
  '--btn-primary-gradient',
  '--btn-accent-gradient',
  '--btn-success-gradient',
  '--btn-danger-gradient',
  '--btn-warning-gradient',
  '--btn-nav-icon-badge-gradient',
]

/** Clear inline overrides so stylesheet defaults apply again. */
export function resetBrandTheme() {
  const root = document.documentElement
  Object.values(COLOR_VAR_MAP).forEach((cssVar) => {
    root.style.removeProperty(cssVar)
  })
  ;[
    '--clinch-primary-rgb',
    '--clinch-navy-rgb',
    '--gradient-refresh-blue',
    '--gradient-primary',
    '--gradient-refresh-green',
    '--clinch-green-dark',
    '--background',
    '--ui-bg-secondary',
    ...SIDEBAR_CSS_PROPS,
    ...NAVBAR_CSS_PROPS,
    ...BUTTON_CSS_PROPS,
    ...PAGE_TYPOGRAPHY_CSS_PROPS,
    ...TEXT_CSS_PROPS,
  ].forEach((prop) => root.style.removeProperty(prop))

  delete root.dataset.brandAppName
  delete root.dataset.brandLogoUrl
  delete root.dataset.sidebarStyle
  delete root.dataset.sidebarRadius
  delete root.dataset.sidebarDensity
  delete root.dataset.sidebarLayout
  delete root.dataset.sidebarProfileStyle
  delete root.dataset.sidebarProfilePosition
  delete root.dataset.navbarStyle
  delete root.dataset.brandTypography

  clearBrandThemeStorage()
  const defaults = normalizeBrandTheme(DEFAULT_CLINCH_THEME)
  applyBrandTheme(defaults)
  return defaults
}

/**
 * Bootstrap on app start for the current Host.
 * Later: prefer TenantBranding API, fall back to host-scoped storage.
 */
export async function loadBrandTheme(opts = {}) {
  const host = opts.host || getBrandThemeHost()
  try {
    const { default: api } = await import('@/utils/api')
    const res = await api.get('/branding/', {
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
    const themeJson = res.data?.theme_json
    if (themeJson && typeof themeJson === 'object' && Object.keys(themeJson).length > 0) {
      const normalized = normalizeBrandTheme(themeJson)
      saveBrandThemeToStorage(normalized, host)
      return normalized
    }
  } catch {
    // Fall back to host-scoped localStorage when API unavailable or empty.
  }
  return loadBrandThemeFromStorage(host)
}

export async function loadBrandThemeForTenant(tenantId) {
  const { default: api } = await import('@/utils/api')
  const res = await api.get(`/admin/tenants/${tenantId}/branding/`)
  const themeJson = res.data?.theme_json
  if (themeJson && typeof themeJson === 'object' && Object.keys(themeJson).length > 0) {
    return normalizeBrandTheme(themeJson)
  }
  return normalizeBrandTheme(DEFAULT_CLINCH_THEME)
}

export async function saveBrandThemeToApi(theme, opts = {}) {
  const normalized = normalizeBrandTheme(theme)
  const { default: api } = await import('@/utils/api')
  const tenantId = opts.tenantId
  if (tenantId) {
    await api.put(`/admin/tenants/${tenantId}/branding/`, { theme_json: normalized })
    return normalized
  }
  await api.put('/branding/', { theme_json: normalized })
  saveBrandThemeToStorage(normalized)
  return normalized
}

export function bootstrapBrandTheme() {
  const theme = loadBrandThemeFromStorage()
  return applyBrandTheme(theme)
}

/** Refresh theme from TenantBranding API after localStorage bootstrap. */
export async function bootstrapBrandThemeAsync() {
  const theme = await loadBrandTheme()
  return applyBrandTheme(theme)
}
